'use client';

import { useState } from 'react';
import Link from 'next/link';
import { SERVICE_TYPES, SERVICE_TYPE_LABELS } from '@/lib/validations/lead';

// ─── Types ────────────────────────────────────────────────────

interface FormState {
  company_name: string;
  contact_name: string;
  email: string;
  phone: string;
  country: string;
  city: string;
  service_types: string[];
  website: string;
  vat_number: string;
  notes: string;
}

const emptyForm: FormState = {
  company_name: '',
  contact_name: '',
  email: '',
  phone: '',
  country: '',
  city: '',
  service_types: [],
  website: '',
  vat_number: '',
  notes: '',
};

// ─── Page ─────────────────────────────────────────────────────

export default function PartnerApplyPage() {
  const [form, setForm] = useState<FormState>(emptyForm);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [serverError, setServerError] = useState('');

  const set = (field: keyof FormState, value: string) =>
    setForm((f) => ({ ...f, [field]: value }));

  const toggleService = (slug: string) => {
    setForm((f) => ({
      ...f,
      service_types: f.service_types.includes(slug)
        ? f.service_types.filter((s) => s !== slug)
        : [...f.service_types, slug],
    }));
  };

  const validate = (): boolean => {
    const e: Record<string, string> = {};
    if (!form.company_name.trim()) e.company_name = 'Company name is required.';
    if (!form.contact_name.trim()) e.contact_name = 'Contact name is required.';
    if (!form.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
      e.email = 'Valid email is required.';
    if (!form.phone.trim()) e.phone = 'Phone is required.';
    if (!form.country.trim()) e.country = 'Country is required.';
    if (!form.city.trim()) e.city = 'City is required.';
    if (form.service_types.length === 0) e.service_types = 'Select at least one service type.';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setServerError('');
    if (!validate()) return;

    setSubmitting(true);
    try {
      const res = await fetch('/api/partner-applications', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...form,
          website: form.website.trim() || null,
          vat_number: form.vat_number.trim() || null,
          notes: form.notes.trim() || null,
        }),
      });
      const json = await res.json();
      if (!res.ok) {
        if (json.details && typeof json.details === 'object') {
          setErrors(json.details as Record<string, string>);
        } else {
          setServerError(json.error || 'Submission failed. Please try again.');
        }
        return;
      }
      setSubmitted(true);
    } catch {
      setServerError('Network error. Please check your connection and try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4">
        <div className="max-w-md w-full text-center">
          <div className="w-14 h-14 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
            <svg className="w-7 h-7 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h1 className="text-2xl font-700 text-foreground mb-2">Application Submitted</h1>
          <p className="text-muted-foreground text-sm leading-relaxed mb-6">
            Thank you for applying to join the LeadCore network. Our team will review your application and get back to you shortly.
          </p>
          <Link href="/" className="text-sm text-primary font-500 hover:underline">
            ← Back to home
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border/60 bg-white px-4 sm:px-6 py-4">
        <div className="max-w-2xl mx-auto flex items-center justify-between">
          <Link href="/" className="font-display text-lg font-700 tracking-tight text-foreground hover:text-primary transition-colors">
            LeadCore
          </Link>
          <span className="text-xs text-muted-foreground font-500">Partner Network</span>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 sm:px-6 py-10">
        <div className="mb-8">
          <h1 className="text-2xl font-700 text-foreground tracking-tight mb-2">Apply to Join Our Partner Network</h1>
          <p className="text-sm text-muted-foreground leading-relaxed">
            Fill out the form below to apply as a service provider. We review all applications manually.
          </p>
        </div>

        {serverError && (
          <div className="mb-5 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
            {serverError}
          </div>
        )}

        <form onSubmit={handleSubmit} noValidate className="space-y-5">
          <div className="bg-white rounded-xl border border-border p-6 space-y-4">
            <h2 className="text-sm font-700 text-foreground">Business Information</h2>

            {/* Company name */}
            <div>
              <label className="block text-xs font-600 text-foreground mb-1">
                Company Name <span className="text-destructive">*</span>
              </label>
              <input
                type="text"
                value={form.company_name}
                onChange={(e) => set('company_name', e.target.value)}
                placeholder="Acme Plumbing Co."
                className={`w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary ${errors.company_name ? 'border-destructive' : 'border-border'}`}
              />
              {errors.company_name && <p className="text-xs text-destructive mt-1">{errors.company_name}</p>}
            </div>

            {/* Contact name */}
            <div>
              <label className="block text-xs font-600 text-foreground mb-1">
                Contact Name <span className="text-destructive">*</span>
              </label>
              <input
                type="text"
                value={form.contact_name}
                onChange={(e) => set('contact_name', e.target.value)}
                placeholder="John Smith"
                className={`w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary ${errors.contact_name ? 'border-destructive' : 'border-border'}`}
              />
              {errors.contact_name && <p className="text-xs text-destructive mt-1">{errors.contact_name}</p>}
            </div>

            {/* Email + Phone */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-600 text-foreground mb-1">
                  Email <span className="text-destructive">*</span>
                </label>
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) => set('email', e.target.value)}
                  placeholder="contact@company.com"
                  className={`w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary ${errors.email ? 'border-destructive' : 'border-border'}`}
                />
                {errors.email && <p className="text-xs text-destructive mt-1">{errors.email}</p>}
              </div>
              <div>
                <label className="block text-xs font-600 text-foreground mb-1">
                  Phone <span className="text-destructive">*</span>
                </label>
                <input
                  type="tel"
                  value={form.phone}
                  onChange={(e) => set('phone', e.target.value)}
                  placeholder="+1 (555) 000-0000"
                  className={`w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary ${errors.phone ? 'border-destructive' : 'border-border'}`}
                />
                {errors.phone && <p className="text-xs text-destructive mt-1">{errors.phone}</p>}
              </div>
            </div>

            {/* Country + City */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-600 text-foreground mb-1">
                  Country <span className="text-destructive">*</span>
                </label>
                <input
                  type="text"
                  value={form.country}
                  onChange={(e) => set('country', e.target.value)}
                  placeholder="United States"
                  className={`w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary ${errors.country ? 'border-destructive' : 'border-border'}`}
                />
                {errors.country && <p className="text-xs text-destructive mt-1">{errors.country}</p>}
              </div>
              <div>
                <label className="block text-xs font-600 text-foreground mb-1">
                  City <span className="text-destructive">*</span>
                </label>
                <input
                  type="text"
                  value={form.city}
                  onChange={(e) => set('city', e.target.value)}
                  placeholder="Austin"
                  className={`w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary ${errors.city ? 'border-destructive' : 'border-border'}`}
                />
                {errors.city && <p className="text-xs text-destructive mt-1">{errors.city}</p>}
              </div>
            </div>

            {/* Website */}
            <div>
              <label className="block text-xs font-600 text-foreground mb-1">
                Website <span className="text-muted-foreground font-400">(optional)</span>
              </label>
              <input
                type="url"
                value={form.website}
                onChange={(e) => set('website', e.target.value)}
                placeholder="https://yourcompany.com"
                className="w-full border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
              />
            </div>

            {/* VAT number */}
            <div>
              <label className="block text-xs font-600 text-foreground mb-1">
                VAT / Tax Number <span className="text-muted-foreground font-400">(optional)</span>
              </label>
              <input
                type="text"
                value={form.vat_number}
                onChange={(e) => set('vat_number', e.target.value)}
                placeholder="US123456789"
                className="w-full border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
              />
            </div>
          </div>

          {/* Service types */}
          <div className="bg-white rounded-xl border border-border p-6">
            <h2 className="text-sm font-700 text-foreground mb-1">
              Service Types <span className="text-destructive">*</span>
            </h2>
            <p className="text-xs text-muted-foreground mb-3">Select all services your company provides.</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {SERVICE_TYPES.map((slug) => {
                const checked = form.service_types.includes(slug);
                return (
                  <label
                    key={slug}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-xs cursor-pointer transition-colors ${
                      checked
                        ? 'border-primary bg-primary/5 text-primary font-600' :'border-border text-foreground hover:border-foreground/30'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleService(slug)}
                      className="sr-only"
                    />
                    <span
                      className={`w-3.5 h-3.5 rounded border flex-shrink-0 flex items-center justify-center ${
                        checked ? 'bg-primary border-primary' : 'border-border'
                      }`}
                    >
                      {checked && (
                        <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                    </span>
                    {SERVICE_TYPE_LABELS[slug]}
                  </label>
                );
              })}
            </div>
            {errors.service_types && <p className="text-xs text-destructive mt-2">{errors.service_types}</p>}
          </div>

          {/* Notes */}
          <div className="bg-white rounded-xl border border-border p-6">
            <h2 className="text-sm font-700 text-foreground mb-1">
              Additional Notes <span className="text-muted-foreground font-400">(optional)</span>
            </h2>
            <textarea
              rows={4}
              value={form.notes}
              onChange={(e) => set('notes', e.target.value)}
              placeholder="Tell us about your company, experience, certifications, or anything else relevant."
              className="w-full border border-border rounded-lg px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
            />
          </div>

          <p className="text-xs text-muted-foreground">
            <span className="text-destructive font-600">*</span> Required fields
          </p>

          <button
            type="submit"
            disabled={submitting}
            className="w-full py-3 bg-primary text-white text-sm font-600 rounded-xl hover:bg-primary/90 disabled:opacity-50 transition-colors"
          >
            {submitting ? 'Submitting…' : 'Submit Application'}
          </button>
        </form>
      </main>
    </div>
  );
}
